import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayoutHeader } from '@client/components/_core/layout/PageLayout/PageLayoutHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { type AiPromptAction, AiPromptEdit } from '@client/components/ai-prompts/AiPromptEdit';
import { ImportAiPromptsModal } from '@client/components/ai-prompts/ImportAiPromptsModal';
import { VersionHistory } from '@client/components/ai-prompts/VersionHistory';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { downloadBlob } from '@client/utils/common/blob';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Space } from 'antd';
import type { Key } from 'antd/es/table/interface';
import { Download, MessageSquareCode, Plus, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAiPromptColumns } from './columns';

export const Route = createFileRoute('/admin-console/ai/ai-prompts')({
    staticData: {
        breadcrumb: {
            title: 'AI Prompts',
        },
        ui: {
            icon: MessageSquareCode,
            title: 'AI Prompts',
            subtitle: 'Create, edit, and organize AI prompts to power intelligent automation.',
            widthVariant: 'fullWidth',
            menuDescription: 'Create and manage AI prompts',
        },
    },
    component: AiPrompts,
});

type StatusFilter = 'all' | 'active' | 'draft' | 'archived';

function AiPrompts() {
    const { trpc, trpcClient, trpcUtils } = useTrpc();
    const { isReadOnly } = useAdminModulePermissions('ai');
    const [aiPromptAction, setAiPromptAction] = useState<AiPromptAction>(['none']);
    const [selectedVersionPromptId, setSelectedVersionPromptId] = useState<bigint | null>(null);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<bigint[]>([]);
    const [queryVariables, setQueryVariables] = usePathState<RouterInputs['aiPrompt']['listAiPrompts']>(
        'query-variables',
        {
            limit: DEFAULT_PAGE_SIZE,
            orderBy: { id: 'desc' },
        },
    );

    // Apply status filter to query
    const queryWithStatus = {
        ...queryVariables,
        includeAllVersions: false, // Explicitly only show latest versions
        statusFilter:
            statusFilter === 'all'
                ? undefined
                : statusFilter === 'active'
                  ? ['PUBLISHED' as const]
                  : statusFilter === 'draft'
                    ? ['DRAFT' as const]
                    : ['ARCHIVED' as const],
    };

    const {
        data: aiPrompts,
        isLoading,
        isFetching,
        refetch,
        error,
    } = trpc.aiPrompt.listAiPrompts.useQuery(queryWithStatus);
    const { mutate: deleteAiPrompt } = trpc.aiPrompt.deleteAiPrompt.useMutation();
    const { mutate: copyAiPrompt } = trpc.aiPrompt.copyAiPrompt.useMutation();

    const columns = useAiPromptColumns({
        onEdit: (id) => setAiPromptAction(['edit', id]),
        onVersionHistory: (id) => setSelectedVersionPromptId(id),
        onCopy: async (id) => {
            return new Promise((resolve, reject) => {
                copyAiPrompt(
                    { idToCopy: id },
                    {
                        onSuccess: () => {
                            void trpcUtils.aiPrompt.listAiPrompts.invalidate();
                            resolve();
                        },
                        onError: (error) => {
                            reject(error);
                        },
                    },
                );
            });
        },
        onDelete: async (id) => {
            return new Promise((resolve, reject) => {
                deleteAiPrompt(
                    { id },
                    {
                        onSuccess: () => {
                            void refetch();
                            resolve();
                        },
                        onError: (error) => {
                            reject(error);
                        },
                    },
                );
            });
        },
        isReadOnly,
    });

    const handleExport = async () => {
        try {
            setIsExporting(true);
            const result = await trpcClient.aiPrompt.exportAiPrompts.query({
                ids: selectedRowKeys.length > 0 ? selectedRowKeys : undefined,
                includeArchived: statusFilter === 'archived',
            });
            const blob = new Blob([result.json], { type: 'application/json;charset=utf-8;' });
            downloadBlob(blob, result.filename);
            message.success(`Exported ${result.count} AI prompts`);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsExporting(false);
        }
    };

    const tableProps = usePrismaTable({
        data: aiPrompts?.rows || [],
        total: aiPrompts?.total || 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        queryParams: queryWithStatus,
        onParamsChange: setQueryVariables,
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedRowKeys as unknown as Key[],
            onChange: (keys: Key[]) => {
                setSelectedRowKeys(keys.map((k) => BigInt(String(k))));
            },
        },
    });
    const hasBlockingError = hasBlockingQueryError(error, aiPrompts);

    useEffect(() => {
        if (hasBlockingError) {
            setAiPromptAction(['none']);
            setSelectedVersionPromptId(null);
        }
    }, [hasBlockingError]);

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="ai" />
            <PageLayoutHeader
                features={[
                    {
                        feature: 'search',
                        onSearch(value) {
                            setQueryVariables({
                                ...queryVariables,
                                where: {
                                    ...queryVariables.where,
                                    OR: [{ key: { contains: value } }, { title: { contains: value } }],
                                },
                            });
                        },
                    },
                ]}
                rightBottomAddon={
                    <Space>
                        <SegmentedControl<StatusFilter>
                            value={statusFilter}
                            onChange={setStatusFilter}
                            size="small"
                            options={[
                                { label: 'All', value: 'all', tooltip: 'Show all prompts' },
                                { label: 'Published', value: 'active', tooltip: 'Show published prompts' },
                                { label: 'Draft', value: 'draft', tooltip: 'Show draft prompts' },
                                { label: 'Archived', value: 'archived', tooltip: 'Show archived prompts' },
                            ]}
                            aria-label="Filter AI prompts by status"
                            variant="bordered"
                        />
                        <Button type="secondary" icon={<Upload />} onClick={handleExport} loading={isExporting}>
                            {selectedRowKeys.length > 0 ? `Export selected (${selectedRowKeys.length})` : 'Export all'}
                        </Button>
                        {!isReadOnly && (
                            <>
                                <Button type="secondary" icon={<Download />} onClick={() => setIsImportModalOpen(true)}>
                                    Import
                                </Button>
                                <Button type="primary" icon={<Plus />} onClick={() => setAiPromptAction(['create'])}>
                                    Create prompt
                                </Button>
                            </>
                        )}
                    </Space>
                }
            />

            {hasBlockingError ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    retrying={isFetching}
                    title="Couldn't load AI prompts"
                />
            ) : (
                <>
                    {error && (
                        <InlineQueryError
                            error={error}
                            onRetry={() => void refetch()}
                            retrying={isFetching}
                            title="Couldn't refresh AI prompts"
                            compact
                        />
                    )}
                    <PrismaTable {...tableProps} size="small" scroll={{ x: 'max-content' }} />
                </>
            )}

            {!hasBlockingError && (
                <>
                    <AiPromptEdit
                        action={aiPromptAction}
                        onClose={() => {
                            setAiPromptAction(['none']);
                            void refetch();
                        }}
                        isReadOnly={isReadOnly}
                    />

                    <VersionHistory
                        promptId={selectedVersionPromptId}
                        onClose={() => setSelectedVersionPromptId(null)}
                        onRestore={() => void refetch()}
                    />
                </>
            )}

            <ImportAiPromptsModal
                open={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onSuccess={() => void refetch()}
            />
        </PageController>
    );
}
